import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { Snackbar, useTheme } from '@mui/material'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import hipersendService from '../../services/hipersend'
import { tokensDoDisparo } from '../shared/disparo/tokens'
import LinkWithQuery from '../LinkWithQuery'
import {
  ESTADO_INICIAL,
  TELAS_COM_CONSULTA,
  codigoSoletrado,
  detalhesDaConexao,
  fracaoDoAnel,
  fraseDaConexao,
  fraseDoVencido,
  linkDaGla,
  mmss,
  partesDoCodigo,
  poucoTempo,
  reduzir,
  segundosRestantes,
  textoDoAviso
} from './regras'
import './conectar-conecta.css'

// "Configurações › Conectar ao GLA Conecta" — onde o cliente do HiperWaba gera o
// código que cola no Conecta ("Já uso o HiperWaba"). Fiel às telas hw-inicio,
// hw-codigo, hw-vencido e hw-conectado do mockup aprovado. O master decide tudo;
// a regra de exibição mora em regras.js (testada à parte).

// Enquanto há código na tela, o master é consultado de tempos em tempos: é
// assim que a tela percebe que o Conecta usou o código (e vira "conectado") ou
// que outra aba gerou um novo.
const INTERVALO_DA_CONSULTA_MS = 5000
// Depois de usado, a conexão leva segundos no Conecta. Passado isto sem
// resposta, a tela para de perguntar e oferece "Ver como ficou".
const TETO_DO_USADO_MS = 3 * 60 * 1000
// Fora do componente de propósito: uma função nova a cada render refaria a
// consulta ao master sem parar.
const AGORA = () => Date.now()

function Botao({ tipo = 'pri', icone: Icone, children, ...props }) {
  return (
    <button type="button" className={`gc-btn ${tipo}`} {...props}>
      {Icone && <Icone className="gc-i" aria-hidden="true" />}
      {children}
    </button>
  )
}

function Cabecalho({ selo }) {
  return (
    <div className="gc-cab">
      <div className="gc-cab-linha">
        <h1>Conectar ao GLA Conecta</h1>
        {selo}
      </div>
      <p>Use este mesmo número oficial para atender seus clientes no GLA Conecta, sem precisar de técnico.</p>
    </div>
  )
}

function Selo({ tom = 'ok', icone: Icone = ScheduleRoundedIcon, children }) {
  return (
    <span className={`gc-status ${tom}`}>
      <Icone className="gc-i" aria-hidden="true" />
      {children}
    </span>
  )
}

function OQueMuda() {
  return (
    <div className="gc-bloco">
      <h2 className="gc-rot">O que muda quando conectar</h2>
      <ul className="gc-muda">
        <li>
          <span className="gc-ico"><SendOutlinedIcon aria-hidden="true" /></span>
          <div><b>Seus disparos continuam iguais</b><span>Cobranças, avisos e agendamentos seguem saindo daqui, do mesmo jeito.</span></div>
        </li>
        <li>
          <span className="gc-ico info"><ChatBubbleOutlineRoundedIcon aria-hidden="true" /></span>
          <div><b>As respostas dos clientes vão para o atendimento</b><span>Quando alguém responder, a conversa chega no GLA Conecta para a sua equipe atender.</span></div>
        </li>
        <li>
          <span className="gc-ico aviso"><PaidOutlinedIcon aria-hidden="true" /></span>
          <div><b>O atendimento passa a ser cobrado nesta API</b><span>Cada conversa que sua equipe responder pelo Conecta usa o saldo desta API, pela tabela de atendimento.</span></div>
        </li>
      </ul>
    </div>
  )
}

function Anel({ restante, total }) {
  const r = 30
  const circ = 2 * Math.PI * r
  const frac = fracaoDoAnel(restante, total)
  return (
    <div className={`gc-anel${poucoTempo(restante) ? ' pouco' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 72 72">
        <circle className="trilho" cx="36" cy="36" r={r} fill="none" strokeWidth="5" />
        <circle
          className="prog"
          cx="36"
          cy="36"
          r={r}
          fill="none"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circ.toFixed(2)}
          strokeDashoffset={(circ * (1 - frac)).toFixed(2)}
        />
      </svg>
      <span>{mmss(restante)}</span>
    </div>
  )
}

function CodigoNaCaixa({ codigo, apagado }) {
  if (!codigo) {
    return (
      <div className={`gc-codigo oculto${apagado ? ' apagado' : ''}`} aria-label={apagado ? 'Código vencido' : 'Código escondido'}>
        <span aria-hidden="true">••••</span><span className="traco" aria-hidden="true">-</span><span aria-hidden="true">••••</span>
      </div>
    )
  }
  const [a, b] = partesDoCodigo(codigo)
  return (
    <div className={`gc-codigo${apagado ? ' apagado' : ''}`} aria-label={apagado ? 'Código vencido' : `Código ${codigoSoletrado(codigo)}`}>
      <span aria-hidden="true">{a}</span><span className="traco" aria-hidden="true">-</span><span aria-hidden="true">{b}</span>
    </div>
  )
}

function AgoraNoConecta() {
  return (
    <div className="gc-bloco">
      <h2 className="gc-rot">Agora, no GLA Conecta</h2>
      <ol className="gc-passos">
        <li>Entre em <b>Canais de atendimento</b>.</li>
        <li>Clique em <b>Já uso o HiperWaba</b>.</li>
        <li>Cole o código e confirme.</li>
      </ol>
      <div className="gc-alerta aviso">
        <ShieldOutlinedIcon className="gc-i" aria-hidden="true" />
        <div>Não mande este código para ninguém de fora da sua empresa. Quem tiver o código consegue ligar este número a outro atendimento. Se gerar outro, este para de valer na hora.</div>
      </div>
      <p className="gc-nota gc-sem-botao">Se o botão “Já uso o HiperWaba” não aparecer no seu Conecta, fale com a GLA.</p>
    </div>
  )
}

async function copiarTexto(texto) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(texto)
      return true
    }
  } catch (e) {
    // cai no jeito antigo logo abaixo
  }
  try {
    const campo = document.createElement('textarea')
    campo.value = texto
    campo.setAttribute('readonly', '')
    campo.style.position = 'fixed'
    campo.style.opacity = '0'
    document.body.appendChild(campo)
    campo.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(campo)
    return ok
  } catch (e) {
    return false
  }
}

export default function ConectarConecta({
  api,
  secret,
  servico = hipersendService.conecta,
  agora: relogio = AGORA,
  numeroDaGla = process.env.REACT_APP_GLA_WHATSAPP
}) {
  const theme = useTheme()
  const [estado, enviar] = useReducer(reduzir, ESTADO_INICIAL)
  const [agora, setAgora] = useState(relogio)
  const [gerando, setGerando] = useState(false)
  const [copiado, setCopiado] = useState(false)
  const [recado, setRecado] = useState('')
  const [anuncio, setAnuncio] = useState('')
  const [consultaParada, setConsultaParada] = useState(false)
  const tituloDoCodigo = useRef(null)
  const vivo = useRef(true)
  const anunciouPouco = useRef(false)
  // Cada "Gerar" sobe a geração. Uma consulta que saiu antes dele (ou durante)
  // descreve o código antigo: sem essa conta, a resposta atrasada diria "gerado
  // em outra aba" ou "foi usado no Conecta" sobre o código novo.
  const geracao = useRef(0)
  const gerandoAgora = useRef(false)

  useEffect(() => {
    vivo.current = true
    return () => { vivo.current = false }
  }, [])

  const consultar = useCallback(async () => {
    if (!api || !secret || gerandoAgora.current) return
    const geracaoNaSaida = geracao.current
    const aindaVale = () => vivo.current && !gerandoAgora.current && geracao.current === geracaoNaSaida
    try {
      const resposta = await servico.estado(api, secret)
      if (!aindaVale()) return
      const t = relogio()
      setAgora(t)
      enviar({ tipo: 'estado', resposta, agora: t })
    } catch (erro) {
      if (aindaVale()) enviar({ tipo: 'falhaAoCarregar', erro })
    }
  }, [api, secret, servico, relogio])

  useEffect(() => { consultar() }, [consultar])

  // Relógio da contagem: só enquanto há código valendo.
  const contando = estado.tela === 'codigo' || estado.tela === 'codigo-oculto'
  useEffect(() => {
    if (!contando) return undefined
    setAgora(relogio())
    const id = setInterval(() => {
      const t = relogio()
      setAgora(t)
      enviar({ tipo: 'tique', agora: t })
    }, 1000)
    return () => clearInterval(id)
  }, [contando, relogio])

  // Consulta de acompanhamento (o Conecta usou? outra aba gerou outro?).
  const acompanhando = TELAS_COM_CONSULTA.includes(estado.tela) && !consultaParada
  useEffect(() => {
    if (!acompanhando) return undefined
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      consultar()
    }, INTERVALO_DA_CONSULTA_MS)
    return () => clearInterval(id)
  }, [acompanhando, consultar])

  useEffect(() => {
    if (estado.tela !== 'usado') {
      setConsultaParada(false)
      return undefined
    }
    const id = setTimeout(() => setConsultaParada(true), TETO_DO_USADO_MS)
    return () => clearTimeout(id)
  }, [estado.tela])

  const restante = contando ? Math.min(estado.total, segundosRestantes(estado.venceEm, agora)) : 0

  // Avisos para quem usa leitor de tela: o relógio em si não fala a cada segundo.
  useEffect(() => {
    if (contando && poucoTempo(restante) && !anunciouPouco.current) {
      anunciouPouco.current = true
      setAnuncio('Faltam 2 minutos para o código vencer.')
    }
    if (!contando) anunciouPouco.current = false
  }, [contando, restante])
  useEffect(() => {
    if (estado.tela === 'vencido') setAnuncio(estado.motivoDoVencido === 'outro' ? 'Este código parou de valer.' : 'O código venceu.')
    if (estado.tela === 'usado') setAnuncio('O código foi usado no GLA Conecta.')
    if (estado.tela === 'conectado') setAnuncio('Número conectado ao GLA Conecta.')
  }, [estado.tela, estado.motivoDoVencido])

  const gerar = async () => {
    if (gerando || !api || !secret) return
    const eraOutro = Boolean(estado.codigo) || estado.tela === 'codigo-oculto' || estado.tela === 'vencido' || estado.tela === 'usado'
    setGerando(true)
    geracao.current += 1
    gerandoAgora.current = true
    enviar({ tipo: 'limparAviso' })
    try {
      const resposta = await servico.gerarCodigo(api, secret)
      if (!vivo.current) return
      // O relógio da tela anda junto: sem isso o 1º quadro mostrava 10:01.
      const t = relogio()
      setAgora(t)
      enviar({ tipo: 'gerado', resposta, agora: t })
      setCopiado(false)
      setRecado(eraOutro ? 'Código novo gerado. O anterior parou de valer.' : 'Código gerado. Ele vale por 10 minutos.')
      // Leva o foco ao código: quem navega pelo teclado ou leitor de tela ouve o novo.
      setTimeout(() => tituloDoCodigo.current && tituloDoCodigo.current.focus(), 0)
    } catch (erro) {
      if (vivo.current) enviar({ tipo: 'falhaAoGerar', erro })
    } finally {
      gerandoAgora.current = false
      if (vivo.current) setGerando(false)
    }
  }

  const copiar = async () => {
    if (!estado.codigo) return
    const ok = await copiarTexto(estado.codigo)
    if (!vivo.current) return
    if (ok) {
      setCopiado(true)
      setRecado('Código copiado. Agora cole no GLA Conecta.')
      setTimeout(() => vivo.current && setCopiado(false), 2000)
    } else {
      setRecado('Não deu para copiar sozinho. Selecione o código e copie.')
    }
  }

  const verComoFicou = () => {
    setConsultaParada(false)
    consultar()
  }

  const link = linkDaGla(numeroDaGla)
  const falarComAGla = link ? (
    <a className="gc-btn sec" href={link} target="_blank" rel="noopener noreferrer">
      <WhatsAppIcon className="gc-i" aria-hidden="true" />
      Falar com a GLA
    </a>
  ) : null

  const aviso = textoDoAviso(estado.aviso)
  const blocoDoAviso = aviso ? (
    <div className="gc-alerta erro" role="alert">
      <WarningAmberRoundedIcon className="gc-i" aria-hidden="true" />
      <div>{aviso}</div>
    </div>
  ) : null

  let conteudo = null
  switch (estado.tela) {
    case 'carregando':
      conteudo = (
        <div className="gc-cartao" aria-busy="true">
          <Cabecalho />
          <div className="gc-bloco">
            <div className="gc-esqueleto" style={{ width: '42%' }} />
            <div className="gc-esqueleto" style={{ width: '88%' }} />
            <div className="gc-esqueleto" style={{ width: '76%' }} />
          </div>
          <p className="sr-only" role="status">Carregando…</p>
        </div>
      )
      break

    case 'inicio':
      conteudo = (
        <div className="gc-cartao">
          <Cabecalho />
          <OQueMuda />
          <div className="gc-bloco">
            <h2 className="gc-rot">Como funciona</h2>
            <ol className="gc-passos">
              <li>Você gera um código aqui. Ele vale por 10 minutos e só pode ser usado uma vez.</li>
              <li>No GLA Conecta, abra <b>Canais de atendimento → Já uso o HiperWaba</b> e cole o código.</li>
              <li>Confira o número e a empresa e confirme. Pronto.</li>
            </ol>
          </div>
          <div className="gc-bloco">
            {blocoDoAviso}
            <div className="gc-acoes">
              <Botao icone={LinkRoundedIcon} onClick={gerar} disabled={gerando}>{gerando ? 'Gerando…' : 'Gerar código'}</Botao>
              <span className="gc-nota gc-nota-ico"><LockOutlinedIcon className="gc-i" aria-hidden="true" /> Só quem entra neste painel consegue gerar o código.</span>
            </div>
          </div>
        </div>
      )
      break

    case 'codigo':
    case 'codigo-oculto': {
      const oculto = estado.tela === 'codigo-oculto'
      conteudo = (
        <div className="gc-cartao">
          <Cabecalho selo={<Selo>Código ativo</Selo>} />
          <div className="gc-bloco">
            <h2 className="gc-rot" ref={tituloDoCodigo} tabIndex={-1}>Seu código de conexão</h2>
            <div className="gc-codigo-box" role="timer" aria-label={`Tempo restante: ${mmss(restante)}`}>
              <div className="gc-codigo-txt">
                <CodigoNaCaixa codigo={oculto ? null : estado.codigo} />
                <div className="gc-nota" style={{ marginTop: 6 }}>
                  {oculto
                    ? <>Por segurança, o código só aparece quando é gerado. Vale por mais <b>{mmss(restante)}</b>.</>
                    : <>Vale por <b>{mmss(restante)}</b> · uso único</>}
                </div>
              </div>
              <Anel restante={restante} total={estado.total} />
            </div>
            {blocoDoAviso}
            <div className="gc-acoes" style={{ marginTop: 14 }}>
              {oculto ? (
                <Botao icone={AutorenewRoundedIcon} onClick={gerar} disabled={gerando}>{gerando ? 'Gerando…' : 'Gerar outro para ver'}</Botao>
              ) : (
                <>
                  <Botao icone={copiado ? CheckRoundedIcon : ContentCopyRoundedIcon} onClick={copiar}>{copiado ? 'Copiado!' : 'Copiar código'}</Botao>
                  <Botao tipo="sec" icone={AutorenewRoundedIcon} onClick={gerar} disabled={gerando}>{gerando ? 'Gerando…' : 'Gerar outro'}</Botao>
                </>
              )}
            </div>
          </div>
          <AgoraNoConecta />
        </div>
      )
      break
    }

    case 'vencido':
      conteudo = (
        <div className="gc-cartao">
          <Cabecalho selo={<Selo tom="vencido">{estado.motivoDoVencido === 'outro' ? 'Código substituído' : 'Código vencido'}</Selo>} />
          <div className="gc-bloco">
            <h2 className="gc-rot">Seu código de conexão</h2>
            <div className="gc-codigo-box">
              <div className="gc-codigo-txt">
                <CodigoNaCaixa codigo={estado.codigo} apagado />
                <div className="gc-nota" style={{ marginTop: 6 }}>{fraseDoVencido(estado.motivoDoVencido)}</div>
              </div>
            </div>
            {blocoDoAviso}
            <div className="gc-acoes" style={{ marginTop: 14 }}>
              <Botao icone={AutorenewRoundedIcon} onClick={gerar} disabled={gerando}>{gerando ? 'Gerando…' : 'Gerar outro'}</Botao>
            </div>
          </div>
          <OQueMuda />
        </div>
      )
      break

    case 'usado':
      conteudo = (
        <div className="gc-cartao">
          <Cabecalho selo={<Selo tom="info" icone={InfoOutlinedIcon}>Código usado</Selo>} />
          <div className="gc-bloco">
            <div className="gc-alerta info">
              <CheckRoundedIcon className="gc-i" aria-hidden="true" />
              <div><b>O código foi usado no GLA Conecta.</b> A conexão termina por lá em alguns segundos e aparece aqui assim que ficar pronta.</div>
            </div>
            <p className="gc-nota" style={{ marginTop: 12 }}>Se a conexão parar no meio, o próprio Conecta mostra como continuar. Não foi você? Fale com a GLA.</p>
            {blocoDoAviso}
            <div className="gc-acoes" style={{ marginTop: 14 }}>
              {consultaParada && <Botao icone={AutorenewRoundedIcon} onClick={verComoFicou}>Ver como ficou</Botao>}
              {falarComAGla}
            </div>
          </div>
        </div>
      )
      break

    case 'conectado': {
      const conexao = estado.conexao || {}
      conteudo = (
        <div className="gc-cartao">
          <Cabecalho selo={<Selo icone={CheckRoundedIcon}>Conectado</Selo>} />
          <div className="gc-bloco">
            <div className="gc-alerta info">
              <CheckRoundedIcon className="gc-i" aria-hidden="true" />
              <div><b>{fraseDaConexao(conexao)}</b> Os disparos continuam saindo daqui; as respostas dos clientes chegam lá.</div>
            </div>
          </div>
          <div className="gc-bloco">
            <h2 className="gc-rot">Detalhes da conexão</h2>
            <dl className="gc-dados">
              {detalhesDaConexao(conexao).map(l => (
                <div key={l.rotulo}><dt>{l.rotulo}</dt><dd>{l.valor}</dd></div>
              ))}
            </dl>
          </div>
          <div className="gc-bloco">
            <div className="gc-alerta aviso">
              <WarningAmberRoundedIcon className="gc-i" aria-hidden="true" />
              <div><b>Não reconhece esta conexão?</b> Fale com a GLA agora. Para trocar de empresa ou desconectar, também é com a GLA — assim nenhuma conversa se perde no caminho.</div>
            </div>
            {falarComAGla && <div className="gc-acoes" style={{ marginTop: 14 }}>{falarComAGla}</div>}
          </div>
        </div>
      )
      break
    }

    case 'indisponivel':
      conteudo = (
        <div className="gc-cartao">
          <Cabecalho />
          <div className="gc-bloco">
            <div className="gc-alerta info">
              <InfoOutlinedIcon className="gc-i" aria-hidden="true" />
              <div><b>A conexão com o GLA Conecta ainda não está disponível para esta API.</b> Ela vale para a API oficial da Meta com o número já conectado. Se você já usa o GLA Conecta, fale com a GLA.</div>
            </div>
            <div className="gc-acoes" style={{ marginTop: 14 }}>
              <LinkWithQuery to="/settings" className="gc-btn sec">Voltar às configurações</LinkWithQuery>
              {falarComAGla}
            </div>
          </div>
        </div>
      )
      break

    default:
      conteudo = (
        <div className="gc-cartao">
          <Cabecalho />
          <div className="gc-bloco">
            <div className="gc-alerta erro" role="alert">
              <WarningAmberRoundedIcon className="gc-i" aria-hidden="true" />
              <div><b>Não conseguimos carregar esta tela agora.</b> Confira a internet e tente de novo em instantes.</div>
            </div>
            <div className="gc-acoes" style={{ marginTop: 14 }}>
              <Botao icone={AutorenewRoundedIcon} onClick={() => { enviar({ tipo: 'recarregar' }); consultar() }}>Tentar de novo</Botao>
            </div>
          </div>
        </div>
      )
  }

  return (
    <div className="gc" data-modo={theme.palette.mode === 'dark' ? 'escuro' : 'claro'} style={tokensDoDisparo(theme)}>
      <div className="gc-cont">
        <nav className="gc-trilha" aria-label="Você está em">
          <LinkWithQuery to="/settings">Configurações</LinkWithQuery>
          <span aria-hidden="true">›</span>
          <b aria-current="page">Conectar ao GLA Conecta</b>
        </nav>
        {conteudo}
      </div>
      <p className="sr-only" aria-live="polite">{anuncio}</p>
      <Snackbar
        open={Boolean(recado)}
        onClose={() => setRecado('')}
        autoHideDuration={3500}
        message={recado}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 88, lg: 24 } }}
      />
    </div>
  )
}
